import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, lazy, Suspense } from 'react';
import api from '../services/api';
import { LogOut, ShoppingCart, Gamepad2, Shield, ScanLine, Loader2 } from 'lucide-react';
import { getBackendUrl } from '../utils/config';
import { Button } from './ui/button';

// Componentes carregados imediatamente (mais usados)
import AdminDashboardTab from './AdminDashboardTab';
import ColaboradoresTab from './ColaboradoresTab';
import VendasTab from './VendasTab';
import AlertaValidadesHoje from './AlertaValidadesHoje';
import NotificacoesDropdown from './NotificacoesDropdown';
import AdminNavigationTabs from './AdminNavigationTabs';
import AdminSubTabs from './AdminSubTabs';
import useWebSocket from '../hooks/useWebSocket';
import useBoxReservationWatcher from '../hooks/useBoxReservationWatcher';

// Lazy loading para componentes menos frequentes
const ObjetivosTab = lazy(() => import('./ObjetivosTab'));
const AjustePontosTab = lazy(() => import('./AjustePontosTab'));
const TurnosDiaTab = lazy(() => import('./TurnosDiaTab'));
const StocksTab = lazy(() => import('./StocksTab'));
const FechosTurnosTab = lazy(() => import('./FechosTurnosTab'));
const RevendasTab = lazy(() => import('./RevendasTab'));
const RegistoPontoTab = lazy(() => import('./RegistoPontoTab'));
const TarefasTab = lazy(() => import('./TarefasTab'));
const TarefasTemplatesSimplificado = lazy(() => import('./TarefasTemplatesSimplificado'));
// iter289 — Relatório de Turno substitui Tarefas por Turno
import RelatorioTurnoTab from './RelatorioTurnoTab';
const ResultadosTab = lazy(() => import('./ResultadosTab'));
const TransferenciasTab = lazy(() => import('./TransferenciasTab'));
const ValidadesTab = lazy(() => import('./ValidadesTab'));
const CatalogoXdTab = lazy(() => import('./CatalogoXdTab'));
const StockUnificadoTab = lazy(() => import('./StockUnificadoTab'));
const AnaliseComercialTab = lazy(() => import('./AnaliseComercialTab'));
const DecisoesTab = lazy(() => import('./DecisoesTab'));
const ResumoDiarioTab = lazy(() => import('./ResumoDiarioTab'));
const HorarioTab = lazy(() => import('./HorarioTabCompleto'));
const FluxoCaixaTab = lazy(() => import('./FluxoCaixaTab'));
const MonitorizacaoTab = lazy(() => import('./MonitorizacaoTab'));
const ObjetivosVendasTab = lazy(() => import('./ObjetivosVendasTab'));
const AnaliseTab = lazy(() => import('./AnaliseTab'));
const AlertaFaltasPendentes = lazy(() => import('./AlertaFaltasPendentes'));
const AlertaObjetivosConcluidosAdmin = lazy(() => import('./AlertaObjetivosConcluidosAdmin'));
const RegistarVendaModal = lazy(() => import('./RegistarVendaModal'));
const BackupConfigTab = lazy(() => import('./BackupConfigTab'));
const LimpezaDadosTesteTab = lazy(() => import('./LimpezaDadosTesteTab'));
const SistemaManutencaoTab = lazy(() => import('./SistemaManutencaoTab'));
const HigieneDadosTab = lazy(() => import('./HigieneDadosTab'));
const FidelizacaoHoje = lazy(() => import('./FidelizacaoHoje'));
const CampanhasEmVigorTab = lazy(() => import('./CampanhasEmVigorTab'));
const FidelizacaoClientes = lazy(() => import('./FidelizacaoClientes'));
const FidelizacaoComunicacao = lazy(() => import('./FidelizacaoComunicacao'));
const FidelizacaoAnalise = lazy(() => import('./FidelizacaoAnalise'));
const FidelizacaoConfigAdmin = lazy(() => import('./FidelizacaoConfigAdmin'));
const FidelizacaoAppCliente = lazy(() => import('./FidelizacaoAppCliente'));
const PromocoesTab = lazy(() => import('./PromocoesTab'));
const GestaoDadosTab = lazy(() => import('./GestaoDadosTab'));
const FechoTurnoTab = lazy(() => import('./FechoTurnoTab'));
const DocumentosAdmin = lazy(() => import('./DocumentosAdmin'));
const GestaoColaboradoresTab = lazy(() => import('./GestaoColaboradoresTab'));
const ChatInterno = lazy(() => import('./ChatInterno'));
const MeuPerfilContainer = lazy(() => import('./MeuPerfilContainer'));
const GestaoIPsTab = lazy(() => import('./GestaoIPsTab'));
const AgendaAdmin = lazy(() => import('./AgendaAdmin'));
const ParceriasModule = lazy(() => import('./Parcerias').then(m => ({ default: m.ParceriasModule })));
const ConsultaOperacional = lazy(() => import('./Parcerias/ConsultaOperacional'));
// Reconciliação: agora vive como pill dentro de FidelizacaoConfigAdmin (P2-B routing fix).
// O componente original continua disponível para import directo se necessário.
const MotorRegrasTab = lazy(() => import('./MotorRegrasTab'));
const GamificacaoTab = lazy(() => import('./GamificacaoTab'));
const AnalyticsAppTab = lazy(() => import('./AnalyticsAppTab'));
const PublicarAppTab = lazy(() => import('./PublicarAppTab'));
const CampanhasSegTab = lazy(() => import('./CampanhasSegTab'));
const GestaoClientes = lazy(() => import('./Parcerias/GestaoClientes'));
const ReconciliacaoClientes = lazy(() => import('./ReconciliacaoClientes'));
const ClientesPendentes = lazy(() => import('./ClientesPendentes'));
const GestaoPontos = lazy(() => import('./Parcerias/GestaoPontos'));
const RelatorioFidelizacao = lazy(() => import('./Parcerias/RelatorioFidelizacao'));
const DashboardAdesao = lazy(() => import('./DashboardAdesao'));
const CicloFidelizacaoConfig = lazy(() => import('./CicloFidelizacaoConfig'));
const CampanhasFidelizacaoPanel = lazy(() => import('./CampanhasFidelizacaoPanel'));
const StockHealthBadge = lazy(() => import('./StockHealthBadge'));
const AlertasTab = lazy(() => import('./AlertasTab'));
const XDIntegrationTab = lazy(() => import('./XDIntegrationTab'));
const XDClientesImportTab = lazy(() => import('./XDClientesImportTab'));
const MultiPrecoDesviosTab = lazy(() => import('./MultiPrecoDesviosTab'));
const RFMAnalyticsTab = lazy(() => import('./RFMAnalyticsTab'));
const CampanhasTab = lazy(() => import('./CampanhasTab'));
const MotorComercialTab = lazy(() => import('./MotorComercialTab'));
const LojasGestao = lazy(() => import('./LojasGestao'));
const QuebrasXdTab = lazy(() => import('./QuebrasXdTab'));
const AlertasOperacionaisTab = lazy(() => import('./AlertasOperacionaisTab'));
const AlertasUnificados = lazy(() => import('./AlertasUnificados'));
const OperacionalHojeResumo = lazy(() => import('./OperacionalHojeResumo'));
const VendasExploracaoTab = lazy(() => import('./VendasExploracaoTab'));
const VendasColaboradorTab = lazy(() => import('./VendasColaboradorTab'));
const XdMappingTab = lazy(() => import('./XdMappingTab'));
const GestaoSiteTab = lazy(() => import('./Site/GestaoSiteTab'));
const BoxJaneiraAdmin = lazy(() => import('./Box/BoxJaneiraAdmin'));

import AlertasLoginModal from './AlertasLoginModal';
import TabErrorBoundary from './TabErrorBoundary';

// Componente de loading para Suspense
const TabLoader = () => (
  <div className="flex items-center justify-center py-12">
    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
    <span className="ml-2 text-gray-500">A carregar...</span>
  </div>
);

// Sub-pills dentro de "Pontos & Vales" — separar campanhas operacionais (motor antigo)
// de campanhas avançadas baseadas em gatilhos (motor novo, ex: boas-vindas).
// Em vez de 2 menus separados, são 2 pills no topo da mesma tab.
const PontosValesPills = ({ active, onChange }) => (
  <div className="flex gap-1 bg-muted/50 p-1 rounded-lg w-fit max-w-full overflow-x-auto" data-testid="pontos-vales-pills">
    <button
      onClick={() => onChange('principal')}
      className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap ${active === 'principal' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
      data-testid="pill-pv-principal"
    >
      Motor Principal
    </button>
    <button
      onClick={() => onChange('avancadas')}
      className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap ${active === 'avancadas' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
      data-testid="pill-pv-avancadas"
    >
      Avançadas (Boas-vindas, Aniversário…)
    </button>
    <button
      onClick={() => onChange('ciclos')}
      className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap ${active === 'ciclos' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
      data-testid="pill-pv-ciclos"
    >
      Ciclos & Bónus
    </button>
    <button
      onClick={() => onChange('regras')}
      className={`px-3 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap ${active === 'regras' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
      data-testid="pill-pv-regras"
    >
      Regras Automáticas
    </button>
  </div>
);

const AdminDashboard = ({ user, onLogout }) => {
  // Ler tab da URL se existir
  const urlParams = new URLSearchParams(window.location.search);
  const tabFromUrl = urlParams.get('tab');
  
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'dashboard');
  const [activeJogosSubTab, setActiveJogosSubTab] = useState('fechos-turnos');
  const [activeColaboradoresSubTab, setActiveColaboradoresSubTab] = useState('gestao');
  const [activeHorarioSubTab, setActiveHorarioSubTab] = useState('horarios');
  const [activeMonitorizacaoSubTab, setActiveMonitorizacaoSubTab] = useState('monitorizacao');
  // Pontos & Vales sub-pills (refactor 2026-05): "principal" = motor antigo (pontos/cashback/promocional),
  // "avancadas" = motor novo (boas-vindas, aniversário, etc.) que estava em App Cliente.
  const [pontosValesPill, setPontosValesPill] = useState('principal');
  // Parte 2: Default = "decisoes" (Hoje) — primeira pergunta = "o que tenho de resolver hoje?"
  const [activeProdutosSubTab, setActiveProdutosSubTab] = useState('decisoes');
  const [produtosNavTarget, setProdutosNavTarget] = useState(null);

  // Handler de navegacao inteligente (Decisoes → outros modulos)
  const handleProdutosNavigate = useCallback((target) => {
    // target: { subTab: 'stock', innerTab: 'ruturas' }
    if (target?.subTab) {
      setProdutosNavTarget({ parent: target.subTab, tab: target.innerTab });
      setActiveProdutosSubTab(target.subTab);
      // Limpar apos navegacao
      setTimeout(() => setProdutosNavTarget(null), 500);
    }
  }, []);
  const [activeClientesSubTab, setActiveClientesSubTab] = useState('gestao');
  const [activeFidelizacaoSubTab, setActiveFidelizacaoSubTab] = useState('hoje');
  const [activeComercialSubTab, setActiveComercialSubTab] = useState('parcerias');
  const [activeControloSubTab, setActiveControloSubTab] = useState('hoje');
  const [activeSistemaSubTab, setActiveSistemaSubTab] = useState('xd');
  const [activeWebsiteSubTab, setActiveWebsiteSubTab] = useState('inicio');
  const [activeVendasSubTab, setActiveVendasSubTab] = useState('registo');
  const [parceiraSubTab, setParceiraSubTab] = useState(null);
  const [showRegistarVendaModal, setShowRegistarVendaModal] = useState(false);
  const [showFechoTurnoModal, setShowFechoTurnoModal] = useState(false);
  const [horariosNaoVistos, setHorariosNaoVistos] = useState(0);
  const [perfisPendentes, setPerfisPendentes] = useState(0);
  const [trocasPendentes, setTrocasPendentes] = useState(0);
  const [feriasPendentes, setFeriasPendentes] = useState(0);
  const [avaliacoesPendentes, setAvaliacoesPendentes] = useState(0);
  const [mensagensNaoLidas, setMensagensNaoLidas] = useState(0);
  const [scrollChatToUnread, setScrollChatToUnread] = useState(false);
  const [chatTargetInfo, setChatTargetInfo] = useState(null); // Info para abrir conversa específica
  const [documentosPendentes, setDocumentosPendentes] = useState(0);
  const [boxNovasCount, setBoxNovasCount] = useState(0);
  // Fase 2.5 — Badge de alertas de Clientes (em risco + sem NIF relevantes)
  const [clientesAlertasTotal, setClientesAlertasTotal] = useState(0);

  // Badges Fidelização: Hoje (operacionais) · Clientes (adesões pendentes) · Config (validações pendentes)
  // Só admin (parent tab é admin-only). Usa endpoints já existentes — sem backend novo.
  const [fidBadges, setFidBadges] = useState({ hoje: 0, clientes: 0, config: 0 });

  // Carregar contadores de Fidelização (badges Hoje/Clientes/Config). Apenas admin.
  useEffect(() => {
    if (user?.role !== 'admin') return;
    let cancelled = false;
    const carregar = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const base = process.env.REACT_APP_BACKEND_URL;
        const [r1, r2, r3] = await Promise.allSettled([
          fetch(`${base}/api/parcerias/reconciliacao/resumo?dias=30`, { headers }),
          fetch(`${base}/api/parcerias/clientes-rfid/pendentes`, { headers }),
          fetch(`${base}/api/parcerias/alertas/campanhas`, { headers }),
        ]);
        let validacoes = 0, pendentes = 0, alertas = 0;
        if (r1.status === 'fulfilled' && r1.value.ok) {
          const d = await r1.value.json();
          validacoes = (d?.contagem?.pendente || 0) + (d?.contagem?.falha || 0);
        }
        if (r2.status === 'fulfilled' && r2.value.ok) {
          const d = await r2.value.json();
          pendentes = Array.isArray(d) ? d.length : (d?.pendentes?.length || 0);
        }
        if (r3.status === 'fulfilled' && r3.value.ok) {
          const d = await r3.value.json();
          alertas = Array.isArray(d) ? d.length : 0;
        }
        if (!cancelled) {
          setFidBadges({
            hoje: validacoes + pendentes + alertas,
            clientes: pendentes,
            config: validacoes,
          });
        }
      } catch (_) { /* silencioso */ }
    };
    carregar();
    const id = setInterval(carregar, 5 * 60 * 1000); // refresh 5min
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.role]);

  // Carregar contador de alertas de Clientes (RFM v2). Apenas admin.
  useEffect(() => {
    if (user?.role !== 'admin') return;
    let cancelled = false;
    const carregar = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clientes/acoes-hoje/badge`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const d = await res.json();
        if (!cancelled) setClientesAlertasTotal(d.total_alertas || 0);
      } catch (_) { /* silencioso */ }
    };
    carregar();
    const id = setInterval(carregar, 5 * 60 * 1000); // refresh 5min
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.role]);

  // Watcher global de novas reservas Box Janeira (som + toast + Notification)
  // Ativo para admin/responsavel/colaborador em qualquer separador.
  useBoxReservationWatcher({
    enabled: ['admin', 'responsavel', 'colaborador'].includes(user?.role),
    onNewReservation: (novas) => {
      setBoxNovasCount(c => c + (novas?.length || 0));
    }
  });

  // Limpar contador de "novas" quando o operador entra no separador Box
  useEffect(() => {
    if (activeTab === 'box' && boxNovasCount > 0) setBoxNovasCount(0);
    // eslint-disable-next-line
  }, [activeTab]);

  // Navegacao cross-modulo: "Criar campanha de vales" a partir de Parcerias abre Fidelizacao -> Campanhas
  useEffect(() => {
    const handler = (e) => {
      const { target, tab, subTab } = e.detail || {};
      if (target === 'fidelizacao-campanhas') {
        setActiveTab('fidelizacao');
        setActiveFidelizacaoSubTab('comunicacao');
      }
      // 🆕 06-06-2026 — Suporte para navegação directa via {tab, subTab}
      // Usado pelo alerta "Colaboradores XD sem mapping" no Dashboard.
      if (tab === 'sistema' && subTab === 'mapping') {
        setActiveTab('sistema');
        setActiveSistemaSubTab('mapping');
      }
    };
    window.addEventListener('janeira:navigate', handler);
    window.addEventListener('admin-navigate', handler);
    return () => {
      window.removeEventListener('janeira:navigate', handler);
      window.removeEventListener('admin-navigate', handler);
    };
  }, []);

  // Retrocompat: bookmark antigo "comercial → campanhas" passa a Fidelização → Campanhas RFM
  useEffect(() => {
    if (activeTab === 'comercial' && activeComercialSubTab === 'campanhas') {
      setActiveTab('fidelizacao');
      setActiveFidelizacaoSubTab('comunicacao-campanhas-rfm');
      setActiveComercialSubTab('parcerias');
    }
  }, [activeTab, activeComercialSubTab]);

  // 🔁 Redirects legacy de sub-tabs Fidelização (22-05-2026 — simplificação visual).
  // Bookmarks/deep-links antigos continuam a funcionar:
  //   hoje → clientes (visão geral integrada)   — A "Visão Geral" passou a viver dentro
  //                                                de Clientes & Adesões. Para já preservamos
  //                                                a sub-tab 'hoje' como destino válido
  //                                                (apenas o LABEL muda para "Visão Geral").
  //   em-vigor → comunicacao
  //   ciclo → pontos
  //   analise → clientes
  //   reconciliacao → ainda é 'reconciliacao' por dentro (o LABEL muda para "Configuração").
  useEffect(() => {
    if (activeTab !== 'fidelizacao') return;
    const map = {
      'visao-geral': 'hoje',          // novo alias → mantém id interno 'hoje'
      'pontos-vales': 'pontos',       // novo alias para id interno 'pontos'
      'configuracao': 'reconciliacao', // novo alias para id interno 'reconciliacao'
      'em-vigor': 'comunicacao',
      'ciclo': 'pontos',              // deep-link legacy → PCV pill "Ciclos & Bónus"
      'regras': 'pontos',             // deep-link legacy → PCV pill "Regras Automáticas"
      'analise': 'clientes',
    };
    const dest = map[activeFidelizacaoSubTab];
    if (dest && dest !== activeFidelizacaoSubTab) {
      setActiveFidelizacaoSubTab(dest);
      // Auto-seleccionar a pill correcta dentro de PCV consoante o deep-link
      if (activeFidelizacaoSubTab === 'ciclo') setPontosValesPill('ciclos');
      else if (activeFidelizacaoSubTab === 'regras') setPontosValesPill('regras');
    }
  }, [activeTab, activeFidelizacaoSubTab]);

  const [alertasParceriasCount, setAlertasParceriasCount] = useState(0);
  const [acessoRestrito, setAcessoRestrito] = useState(false); // Para colaboradores fora do IP da empresa
  const [perfilIncompleto, setPerfilIncompleto] = useState(false);
  const [feriasFilterColaborador, setFeriasFilterColaborador] = useState(null); // Filtro de colaborador para férias (vindo de notificação)
  
  // URL centralizado
  const API_URL = getBackendUrl();

  // Carregar status do perfil (para responsáveis)
  const carregarStatusPerfil = useCallback(async () => {
    if (user.role !== 'responsavel') return;
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${API_URL}/api/menu-colaborador/perfil/meu`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const percentagem = response.data?.percentagem_completo || 0;
      setPerfilIncompleto(percentagem < 100);
    } catch (error) {
      console.error('Erro ao carregar status do perfil:', error);
    }
  }, [API_URL, user.role]);

  // Carregar contagem de horários não vistos
  const carregarHorariosNaoVistos = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${API_URL}/api/horarios-mensais/count/nao-vistos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setHorariosNaoVistos(response.data.count || 0);
    } catch (error) {
      console.error('Erro ao carregar horários não vistos:', error);
    }
  }, [API_URL]);

  // Carregar TODAS as notificações admin (perfis, trocas, férias, avaliações) num único endpoint
  const carregarPerfisPendentes = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${API_URL}/api/menu-colaborador/notificacoes/count/admin`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // O endpoint retorna: { total, perfis, trocas, ferias, avaliacoes }
      setPerfisPendentes(response.data.perfis || 0);
      setTrocasPendentes(response.data.trocas || 0);
      setFeriasPendentes(response.data.ferias || 0);
      setAvaliacoesPendentes(response.data.avaliacoes || 0);
    } catch (error) {
      console.error('Erro ao carregar notificações pendentes:', error);
      // Reset em caso de erro
      setPerfisPendentes(0);
      setTrocasPendentes(0);
      setFeriasPendentes(0);
      setAvaliacoesPendentes(0);
    }
  }, [API_URL]);

  // Carregar contagem de mensagens não lidas
  const carregarMensagensNaoLidas = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${API_URL}/api/chat/nao-lidas/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMensagensNaoLidas(response.data?.count || 0);
    } catch (error) {
      console.error('Erro ao contar mensagens não lidas:', error);
    }
  }, [API_URL]);

  // Carregar contagem de documentos pendentes
  const carregarDocumentosPendentes = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${API_URL}/api/documentos/count/pendentes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDocumentosPendentes(response.data?.count || 0);
    } catch (error) {
      console.error('Erro ao contar documentos pendentes:', error);
    }
  }, [API_URL]);

  // Carregar alertas de parcerias
  const carregarAlertasParcerias = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${API_URL}/api/parcerias/notificacoes/contagem`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const count = response.data?.total || 0;
      setAlertasParceriasCount(count);
      
      // Browser notification for new alerts
      if (count > 0 && 'Notification' in window && Notification.permission === 'granted') {
        if (count > (window.__lastAlertCount || 0)) {
          new Notification('Janeira - Alertas', {
            body: `Tem ${count} alerta(s) de campanha(s) por ler`,
            icon: '/favicon.ico',
            tag: 'parcerias-alertas',
          });
        }
      }
      window.__lastAlertCount = count;
    } catch {
      setAlertasParceriasCount(0);
    }
  }, [API_URL]);

  // NOTA: Trocas, Férias e Avaliações são carregadas pelo endpoint agregado em carregarPerfisPendentes()
  // O endpoint /notificacoes/count/admin já retorna { total, perfis, trocas, ferias, avaliacoes }
  const carregarTrocasPendentes = useCallback(async () => {
    // Esta função agora é redundante - os dados vêm de carregarPerfisPendentes()
    // Mantida para compatibilidade mas não faz nada
  }, []);

  const carregarFeriasPendentes = useCallback(async () => {
    // Esta função agora é redundante - os dados vêm de carregarPerfisPendentes()
    // Mantida para compatibilidade mas não faz nada
  }, []);

  const carregarAvaliacoesPendentes = useCallback(async () => {
    // Esta função agora é redundante - os dados vêm de carregarPerfisPendentes()
    // Mantida para compatibilidade mas não faz nada
  }, []);

  // Verificar acesso por IP (colaboradores fora da empresa só vêem horários)
  const verificarAcessoIP = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/verificar-acesso-ip`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAcessoRestrito(!data.acesso_total);
        
        // Se acesso restrito, redirecionar para horários
        if (!data.acesso_total && activeTab !== 'horario') {
          setActiveTab('horario');
        }
      }
    } catch (error) {
      console.error('Erro ao verificar acesso IP:', error);
      // Em caso de erro, não restringir acesso
      setAcessoRestrito(false);
    }
  }, [activeTab, API_URL]);

  // Carregar TODOS os contadores (simplificado)
  const carregarTodosContadores = useCallback(() => {
    carregarHorariosNaoVistos();
    carregarPerfisPendentes();
    carregarMensagensNaoLidas();
    carregarDocumentosPendentes();
    carregarStatusPerfil();
    carregarAlertasParcerias();
  }, [carregarHorariosNaoVistos, carregarPerfisPendentes, carregarMensagensNaoLidas, carregarDocumentosPendentes, carregarStatusPerfil, carregarAlertasParcerias]);

  useEffect(() => {
    verificarAcessoIP(); // Verificar acesso por IP
    carregarTodosContadores();
    
    // Request browser notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    // POLLING: Atualizar TODOS os contadores a cada 30 segundos como fallback
    // (WebSocket atualiza em tempo real quando disponível)
    const interval = setInterval(carregarTodosContadores, 30000);
    
    return () => clearInterval(interval);
  }, [carregarTodosContadores, verificarAcessoIP]);

  // Backward compatibility: redirect legacy tab names to new tab+subTab structure
  useLayoutEffect(() => {
    const legacyRedirects = {
      'faltas': () => { setActiveTab('produtos'); setActiveProdutosSubTab('stock'); },
      'gestao-colaboradores': () => { setActiveTab('colaboradores'); setActiveColaboradoresSubTab('gestao'); },
      'agenda': () => { /* top-level tab */ },
      'analise': () => { setActiveTab('monitorizacao'); setActiveMonitorizacaoSubTab('comparacoes'); },
      'artigos': () => { setActiveTab('produtos'); setActiveProdutosSubTab('catalogo'); },
      'validades': () => { setActiveTab('produtos'); setActiveProdutosSubTab('validades'); },
      'reconciliacao': () => { setActiveTab('clientes'); setActiveClientesSubTab('reconciliacao'); },
      'xd-clientes': () => { setActiveTab('clientes'); setActiveClientesSubTab('xd-clientes'); },
      'rfm-analytics': () => { setActiveTab('clientes'); setActiveClientesSubTab('rfm'); },
      'parcerias': () => { setActiveTab('comercial'); setActiveComercialSubTab('parcerias'); },
      'campanhas': () => { setActiveTab('comercial'); setActiveComercialSubTab('campanhas'); },
      'motor-comercial': () => { setActiveTab('comercial'); setActiveComercialSubTab('motor'); },
      'alertas': () => { setActiveTab('controlo'); setActiveControloSubTab('alertas'); },
      'ocorrencias': () => { setActiveTab('controlo'); setActiveControloSubTab('alertas'); },
      'tarefas': () => { setActiveTab('documentos'); },
      'xd-integration': () => { setActiveTab('sistema'); setActiveSistemaSubTab('xd'); },
      'backups': () => { setActiveTab('sistema'); setActiveSistemaSubTab('backups'); },
      'gestao-ips': () => { setActiveTab('sistema'); setActiveSistemaSubTab('ips'); },
      'lojas': () => { setActiveTab('sistema'); setActiveSistemaSubTab('lojas'); },
    };
    const redirect = legacyRedirects[activeTab];
    if (redirect) redirect();
  }, [activeTab]);
  
  // 🔔 WEBSOCKET: Notificações em tempo real
  const { isConnected: wsConnected } = useWebSocket({
    onChatMessage: () => {
      // Nova mensagem no chat - atualizar contador
      carregarMensagensNaoLidas();
    },
    onDocumentAdmin: () => {
      // Novo documento carregado
      carregarTodosContadores();
    },
    onObjetivoAdmin: (data) => {
      // Objetivo atingido por um colaborador
      console.log('🎯 Objetivo atingido:', data);
      carregarTodosContadores();
    },
    onUpdateCounters: () => {
      // Pedido para atualizar contadores
      carregarTodosContadores();
    }
  });
  
  // Handler para registo rápido de ponto (irá abrir o modal do RegistoPontoTab)
  const handleRegistarPontoRapido = (tipo) => {
    // Mudar para a aba de ponto
    setActiveTab('ponto');
    // O RegistoPontoTab tem seus próprios botões ENTRADA/SAÍDA
  };
  
  // Mapeamento tab → badge_type (só tabs com sinalética)
  // Quando o user clica numa destas tabs, marcamos a sinalética como vista
  // no backend (POST /api/admin-badges/marcar-visto/{badge_type}). Os endpoints
  // de contagem aplicam overlay last_viewed_at e devolvem 0 a partir desse
  // momento até chegarem itens NOVOS.
  const TAB_BADGE_MAP = React.useMemo(() => ({
    documentos: 'documentos',
    colaboradores: 'colaboradores',
    chat: 'chat',
    horario: 'horarios',
  }), []);

  const marcarBadgeVisto = useCallback(async (tabName) => {
    const badgeType = TAB_BADGE_MAP[tabName];
    if (!badgeType) return;
    try {
      const token = localStorage.getItem('token');
      await api.post(
        `${API_URL}/api/admin-badges/marcar-visto/${badgeType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error(`[ADMIN-BADGES] Erro a marcar ${badgeType} como visto:`, error);
    }
  }, [API_URL, TAB_BADGE_MAP]);

  // Ref para rastrear cliques
  const clickTimerRef = React.useRef(null);
  const clickCountRef = React.useRef(0);
  
  // Handler para duplo clique - abre em nova aba
  const handleTabClick = (tabName, event) => {
    event.preventDefault();
    clickCountRef.current += 1;
    
    if (clickCountRef.current === 1) {
      // Primeiro clique - aguardar para ver se haverá segundo clique
      clickTimerRef.current = setTimeout(async () => {
        // Clique simples - mudar aba
        setActiveTab(tabName);
        setParceiraSubTab(null);
        clickCountRef.current = 0;
        // Sinaléticas: marcar badge como visto no backend ANTES de refrescar
        // os contadores. O overlay last_viewed_at faz a contagem voltar a 0.
        await marcarBadgeVisto(tabName);
        carregarTodosContadores();
      }, 250);
    } else if (clickCountRef.current === 2) {
      // Duplo clique - abrir em nova aba COM token para login automático
      clearTimeout(clickTimerRef.current);
      clickCountRef.current = 0;
      
      // Pegar token do localStorage para passar na URL
      const token = localStorage.getItem('token');
      const currentUrl = window.location.origin + window.location.pathname;
      const newUrl = token 
        ? `${currentUrl}?tab=${tabName}&auth=${encodeURIComponent(token)}`
        : `${currentUrl}?tab=${tabName}`;
      
      window.open(newUrl, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10 no-print">
        <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-shrink">
              <h1 className="text-lg sm:text-2xl font-display font-bold truncate">Painel Administrativo</h1>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">Bem-vindo, {user.nome}</p>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <NotificacoesDropdown 
                onNavigateToChat={(chatInfo) => {
                  setActiveTab('chat');
                  setScrollChatToUnread(true);
                  setChatTargetInfo(chatInfo || null);
                  carregarMensagensNaoLidas();
                }}
                onNavigateToTab={(tab, extraInfo) => {
                  console.log('Navegando para:', tab, extraInfo);
                  
                  // Mapeamentos de navegação
                  if (tab === 'tarefas-turno') {
                    setActiveTab('gerir-tarefas');
                  } else if (tab === 'gestao-colaboradores') {
                    setActiveTab('gestao-colaboradores');
                    // Se tem subTab de férias, configurar o filtro
                    if (extraInfo?.subTab === 'ferias' || extraInfo?.colaboradorId) {
                      setFeriasFilterColaborador(extraInfo);
                    }
                  } else if (tab === 'faltas' || tab === 'ruturas') {
                    // Ruturas de stock — redirecionar para Produtos > Stock
                    setActiveTab('produtos');
                    setActiveProdutosSubTab('stock');
                  } else {
                    setActiveTab(tab);
                  }
                }}
              />
              <Button 
                onClick={() => setActiveTab('operacao')} 
                className="shadow-elegant bg-emerald-600 hover:bg-emerald-700 ring-2 ring-emerald-300 hidden sm:flex"
                size="sm"
                data-testid="btn-operacao-header"
              >
                <ScanLine className="h-4 w-4 sm:mr-2" />
                <span className="hidden md:inline">Operação</span>
              </Button>
              <Button 
                onClick={() => setActiveTab('operacao')} 
                className="shadow-elegant bg-emerald-600 hover:bg-emerald-700 ring-2 ring-emerald-300 sm:hidden p-2"
                size="sm"
                title="Operação"
                data-testid="btn-operacao-header-mobile"
              >
                <ScanLine className="h-4 w-4" />
              </Button>
              <Button onClick={() => setShowFechoTurnoModal(true)} className="shadow-elegant bg-purple-600 hover:bg-purple-700 hidden sm:flex" size="sm">
                <Gamepad2 className="h-4 w-4 sm:mr-2" />
                <span className="hidden md:inline">Registo de Jogos</span>
              </Button>
              <Button onClick={() => setShowFechoTurnoModal(true)} className="shadow-elegant bg-purple-600 hover:bg-purple-700 sm:hidden p-2" size="sm" title="Registo de Jogos">
                <Gamepad2 className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={onLogout} size="sm" className="p-2 sm:px-3">
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Sair</span>
              </Button>
            </div>
          </div>
          
          {/* Aviso de acesso restrito */}
          {acessoRestrito && (
            <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded-lg flex items-center gap-2 text-yellow-800 text-sm">
              <Shield className="h-4 w-4" />
              <span>
                <strong>Acesso Remoto:</strong> Está fora da rede da empresa. Apenas Horário e Chat estão disponíveis.
              </span>
            </div>
          )}
          
          <AdminNavigationTabs
            activeTab={activeTab}
            handleTabClick={handleTabClick}
            user={user}
            acessoRestrito={acessoRestrito}
            horariosNaoVistos={horariosNaoVistos}
            perfisPendentes={perfisPendentes}
            trocasPendentes={trocasPendentes}
            feriasPendentes={feriasPendentes}
            avaliacoesPendentes={avaliacoesPendentes}
            mensagensNaoLidas={mensagensNaoLidas}
            perfilIncompleto={perfilIncompleto}
            documentosPendentes={documentosPendentes}
            boxNovasCount={boxNovasCount}
            clientesAlertas={clientesAlertasTotal}
            carregarPerfisPendentes={carregarPerfisPendentes}
            carregarMensagensNaoLidas={carregarMensagensNaoLidas}
            setScrollChatToUnread={setScrollChatToUnread}
          />
        </div>
      </header>
      
      <AdminSubTabs
        activeTab={activeTab}
        user={user}
        activeJogosSubTab={activeJogosSubTab}
        setActiveJogosSubTab={setActiveJogosSubTab}
        activeColaboradoresSubTab={activeColaboradoresSubTab}
        setActiveColaboradoresSubTab={setActiveColaboradoresSubTab}
        activeMonitorizacaoSubTab={activeMonitorizacaoSubTab}
        setActiveMonitorizacaoSubTab={setActiveMonitorizacaoSubTab}
        activeProdutosSubTab={activeProdutosSubTab}
        setActiveProdutosSubTab={setActiveProdutosSubTab}
        activeClientesSubTab={activeClientesSubTab}
        setActiveClientesSubTab={setActiveClientesSubTab}
        activeFidelizacaoSubTab={activeFidelizacaoSubTab}
        setActiveFidelizacaoSubTab={setActiveFidelizacaoSubTab}
        activeComercialSubTab={activeComercialSubTab}
        setActiveComercialSubTab={setActiveComercialSubTab}
        activeControloSubTab={activeControloSubTab}
        setActiveControloSubTab={setActiveControloSubTab}
        activeSistemaSubTab={activeSistemaSubTab}
        setActiveSistemaSubTab={setActiveSistemaSubTab}
        activeVendasSubTab={activeVendasSubTab}
        setActiveVendasSubTab={setActiveVendasSubTab}
        activeHorarioSubTab={activeHorarioSubTab}
        setActiveHorarioSubTab={setActiveHorarioSubTab}
        alertasParceriasCount={alertasParceriasCount}
        fidBadges={fidBadges}
      />
      
      <main className="container mx-auto px-2 sm:px-4 py-3 sm:py-8">
        <TabErrorBoundary key={`${activeTab}-${activeProdutosSubTab}-${activeFidelizacaoSubTab}-${activeClientesSubTab}-${activeColaboradoresSubTab}-${activeJogosSubTab}`}>
        <Suspense fallback={<TabLoader />}>
        {/* 1. Dashboard */}
        {activeTab === 'dashboard' && (
          <>
            <AlertaFaltasPendentes onNavigate={setActiveTab} />
            <AlertaValidadesHoje />
            <AlertaObjetivosConcluidosAdmin />
            {user.role === 'admin' && <StockHealthBadge />}
            <AdminDashboardTab />
          </>
        )}

        {/* 2. Vendas - sub-tabs */}
        {activeTab === 'vendas' && activeVendasSubTab === 'registo' && <VendasTab />}
        {activeTab === 'vendas' && activeVendasSubTab === 'exploracao' && user.role === 'admin' && <VendasExploracaoTab />}
        {activeTab === 'vendas' && activeVendasSubTab === 'colaboradores' && user.role === 'admin' && <VendasColaboradorTab />}

        {/* 3. Jogos - sub-tabs */}
        {activeTab === 'jogos' && activeJogosSubTab === 'fechos-turnos' && <FechosTurnosTab />}
        {activeTab === 'jogos' && activeJogosSubTab === 'revendas' && <RevendasTab />}
        {activeTab === 'jogos' && activeJogosSubTab === 'stocks' && <StocksTab />}
        {activeTab === 'jogos' && activeJogosSubTab === 'transferencias' && <TransferenciasTab />}
        {activeTab === 'jogos' && activeJogosSubTab === 'resumo' && <ResumoDiarioTab />}

        {/* 4. Ponto */}
        {activeTab === 'ponto' && <RegistoPontoTab />}

        {/* Operacao - Consulta Operacional (sem sub-tabs) */}
        {activeTab === 'operacao' && <ConsultaOperacional user={user} />}

        {/* 5. Relatório de Turno (iter289 — substitui Gerir Tarefas) */}
        {activeTab === 'gerir-tarefas' && <RelatorioTurnoTab user={user} />}

        {/* 6. Documentos (antigo menu Tarefas — modulo documental) */}
        {activeTab === 'documentos' && <DocumentosAdmin user={user} />}

        {/* 6. Colaboradores - sub-tabs */}
        {activeTab === 'colaboradores' && activeColaboradoresSubTab === 'gestao' && (
          <GestaoColaboradoresTab 
            user={user} 
            perfisPendentes={perfisPendentes}
            trocasPendentes={trocasPendentes}
            feriasPendentes={feriasPendentes}
            avaliacoesPendentes={avaliacoesPendentes}
            onRecarregarPerfis={carregarPerfisPendentes}
            feriasFilterColaborador={feriasFilterColaborador}
            onClearFeriasFilter={() => setFeriasFilterColaborador(null)}
          />
        )}

        {/* 7. Horarios */}
        {activeTab === 'horario' && (
          <HorarioTab onHorarioAtualizado={carregarHorariosNaoVistos} />
        )}

        {/* 7.5. Agenda (menu autonomo) */}
        {activeTab === 'agenda' && user.role === 'admin' && (
          <AgendaAdmin user={user} token={localStorage.getItem('token')} />
        )}

        {/* 8. Monitorizacao - sub-tabs */}
        {activeTab === 'monitorizacao' && activeMonitorizacaoSubTab === 'monitorizacao' && <MonitorizacaoTab />}
        {activeTab === 'monitorizacao' && activeMonitorizacaoSubTab === 'comparacoes' && <AnaliseTab />}

        {/* 9. Fluxo de Caixa */}
        {activeTab === 'fluxo-caixa' && <FluxoCaixaTab />}

        {/* 10. Produtos - 6 submenus (reestruturado Fase 1) */}
        {activeTab === 'produtos' && activeProdutosSubTab === 'catalogo' && <CatalogoXdTab />}
        {activeTab === 'produtos' && activeProdutosSubTab === 'validades' && <ValidadesTab />}
        {activeTab === 'produtos' && activeProdutosSubTab === 'stock' && (user.role === 'admin' || user.role === 'responsavel') && <StockUnificadoTab initialTab={produtosNavTarget?.parent === 'stock' ? produtosNavTarget.tab : null} />}
        {activeTab === 'produtos' && activeProdutosSubTab === 'quebras' && (user.role === 'admin' || user.role === 'responsavel') && <QuebrasXdTab user={user} />}
        {activeTab === 'produtos' && activeProdutosSubTab === 'analise' && (user.role === 'admin' || user.role === 'responsavel') && <AnaliseComercialTab user={user} initialTab={produtosNavTarget?.parent === 'analise' ? produtosNavTarget.tab : null} />}
        {activeTab === 'produtos' && activeProdutosSubTab === 'decisoes' && (user.role === 'admin' || user.role === 'responsavel') && <DecisoesTab onNavigate={handleProdutosNavigate} initialTab={produtosNavTarget?.parent === 'decisoes' ? produtosNavTarget.tab : null} />}

        {/* 11. Clientes - sub-tabs (Versão A: Gestão / Higiene de Dados / Análise) */}
        {activeTab === 'clientes' && activeClientesSubTab === 'gestao' && <GestaoClientes user={user} />}
        {activeTab === 'clientes' && activeClientesSubTab === 'higiene' && (user.role === 'admin' || user.role === 'responsavel') && <HigieneDadosTab user={user} />}
        {activeTab === 'clientes' && activeClientesSubTab === 'rfm' && user.role === 'admin' && <RFMAnalyticsTab />}
        {/* Compatibilidade: rotas antigas (caso alguém tenha bookmark/URL guardado) */}
        {activeTab === 'clientes' && activeClientesSubTab === 'pendentes' && (user.role === 'admin' || user.role === 'responsavel') && <HigieneDadosTab user={user} />}
        {activeTab === 'clientes' && activeClientesSubTab === 'reconciliacao' && (user.role === 'admin' || user.role === 'responsavel') && <HigieneDadosTab user={user} />}
        {activeTab === 'clientes' && activeClientesSubTab === 'xd-clientes' && user.role === 'admin' && <XDClientesImportTab />}

        {/* 12. Fidelizacao - 6 sub-tabs operacionais (parent tab é admin-only).
            Retrocompatibilidade dos values antigos é preservada. */}
        {activeTab === 'fidelizacao' && activeFidelizacaoSubTab === 'em-vigor' && (
          <CampanhasEmVigorTab />
        )}

        {/* 12.5 Campanhas em Vigor — tab autónoma para admin + responsável */}
        {activeTab === 'campanhas-em-vigor' && (
          <CampanhasEmVigorTab />
        )}
        {activeTab === 'fidelizacao' && activeFidelizacaoSubTab === 'hoje' && (
          <FidelizacaoHoje onNavigate={(k) => setActiveFidelizacaoSubTab(k)} />
        )}
        {activeTab === 'fidelizacao' && (activeFidelizacaoSubTab === 'clientes' || activeFidelizacaoSubTab === 'adesao') && (
          <FidelizacaoClientes user={user} />
        )}
        {activeTab === 'fidelizacao' && activeFidelizacaoSubTab === 'pontos' && (
          <div className="space-y-4" data-testid="pontos-vales-wrapper">
            <PontosValesPills active={pontosValesPill} onChange={setPontosValesPill} />
            {pontosValesPill === 'principal' && <GestaoPontos user={user} />}
            {pontosValesPill === 'avancadas' && <CampanhasFidelizacaoPanel />}
            {pontosValesPill === 'ciclos' && <CicloFidelizacaoConfig />}
            {pontosValesPill === 'regras' && <MotorRegrasTab />}
          </div>
        )}
        {/* Ciclo & Bónus — sub-tab dedicada (refactor 2026-05) */}
        {activeTab === 'fidelizacao' && activeFidelizacaoSubTab === 'ciclo' && (
          <CicloFidelizacaoConfig />
        )}
        {/* Reconciliação/Configuração — agora encaminhado para FidelizacaoConfigAdmin
            (P2-B routing fix 2026-05-21): subtab-reconciliacao passa a expor as
            pills internas (Reconciliação · Regras · Ciclo · Dados · RGPD · Gamif),
            permitindo aceder ao painel RGPD com colunas por canal. */}
        {activeTab === 'fidelizacao' && (activeFidelizacaoSubTab === 'comunicacao' || activeFidelizacaoSubTab === 'promocoes' || activeFidelizacaoSubTab === 'campanhas' || activeFidelizacaoSubTab === 'publicar' || activeFidelizacaoSubTab === 'comunicacao-campanhas-rfm') && (
          <FidelizacaoComunicacao
            defaultPill={
              activeFidelizacaoSubTab === 'campanhas' ? 'comunicacao' :
              activeFidelizacaoSubTab === 'publicar' ? 'publicar' :
              activeFidelizacaoSubTab === 'comunicacao-campanhas-rfm' ? 'campanhas-rfm' :
              'promocoes'
            }
          />
        )}
        {activeTab === 'fidelizacao' && (activeFidelizacaoSubTab === 'analise' || activeFidelizacaoSubTab === 'relatorio' || activeFidelizacaoSubTab === 'analytics') && (
          <FidelizacaoAnalise
            defaultPill={activeFidelizacaoSubTab === 'analytics' ? 'analytics' : 'relatorio'}
          />
        )}
        {activeTab === 'fidelizacao' && activeFidelizacaoSubTab === 'app-cliente' && (
          <FidelizacaoAppCliente defaultPill="estado" />
        )}
        {/* App Cliente — tab principal autónoma (refactor 2026-05) */}
        {activeTab === 'app-cliente' && (
          <FidelizacaoAppCliente defaultPill="estado" />
        )}
        {activeTab === 'fidelizacao' && (activeFidelizacaoSubTab === 'config' || activeFidelizacaoSubTab === 'reconciliacao' || activeFidelizacaoSubTab === 'regras' || activeFidelizacaoSubTab === 'gamificacao' || activeFidelizacaoSubTab === 'dados' || activeFidelizacaoSubTab === 'rgpd') && (
          <FidelizacaoConfigAdmin
            defaultPill={
              activeFidelizacaoSubTab === 'regras' ? 'regras' :
              activeFidelizacaoSubTab === 'dados' ? 'dados' :
              activeFidelizacaoSubTab === 'gamificacao' ? 'gamificacao' :
              activeFidelizacaoSubTab === 'rgpd' ? 'rgpd' :
              'reconciliacao'
            }
            alertasParceriasCount={alertasParceriasCount}
          />
        )}

        {/* 13. Comercial - sub-tabs (Campanhas movido para Fidelização → Campanhas & Comunicação) */}
        {activeTab === 'comercial' && activeComercialSubTab === 'parcerias' && <ParceriasModule user={user} defaultTab={parceiraSubTab} />}
        {activeTab === 'comercial' && activeComercialSubTab === 'motor' && user.role === 'admin' && <MotorComercialTab />}
        {/* Retrocompat: bookmark `comercial.campanhas` é redireccionado para Fidelização → Campanhas RFM via useEffect (ver topo do componente). */}

        {/* 14. Chat */}
        {activeTab === 'chat' && (
          <ChatInterno 
            user={user} 
            onMensagemEnviada={carregarMensagensNaoLidas}
            scrollToUnread={scrollChatToUnread}
            targetInfo={chatTargetInfo}
            onTargetHandled={() => setChatTargetInfo(null)}
          />
        )}

        {/* 15. Operacional Hoje - sub-tabs (chave interna 'controlo' mantida para retrocompat) */}
        {activeTab === 'controlo' && activeControloSubTab === 'hoje' && (user.role === 'admin' || user.role === 'responsavel') && (
          <OperacionalHojeResumo
            onNavigate={setActiveTab}
            setActiveControloSubTab={setActiveControloSubTab}
          />
        )}
        {/* Vista unificada de Alertas (proposta UX 3 blocos):
            - 'alertas-unificado' → default Operacional (botão novo no menu)
            - 'alertas' (legado, Avisos à Equipa) → defaultTipo='sistema' (preserva expectativa)
            - 'alertas-op' (legado, Monitorização Op) → defaultTipo='operacional' (preserva expectativa)
            Todas renderizam o mesmo componente; apenas muda o filtro inicial. */}
        {activeTab === 'controlo' && activeControloSubTab === 'alertas-unificado' && (user.role === 'admin' || user.role === 'responsavel') && <AlertasUnificados user={user} defaultTipo="operacional" />}
        {activeTab === 'controlo' && activeControloSubTab === 'alertas' && (user.role === 'admin' || user.role === 'responsavel') && <AlertasUnificados user={user} defaultTipo="sistema" />}
        {activeTab === 'controlo' && activeControloSubTab === 'alertas-op' && (user.role === 'admin' || user.role === 'responsavel') && <AlertasUnificados user={user} defaultTipo="operacional" />}

        {/* 16. Sistema - sub-tabs */}
        {activeTab === 'sistema' && activeSistemaSubTab === 'xd' && user.role === 'admin' && <XDIntegrationTab />}
        {activeTab === 'sistema' && activeSistemaSubTab === 'backups' && user.role === 'admin' && <BackupConfigTab />}
        {activeTab === 'sistema' && activeSistemaSubTab === 'ips' && user.role === 'admin' && <GestaoIPsTab />}
        {activeTab === 'sistema' && activeSistemaSubTab === 'mapping' && user.role === 'admin' && <XdMappingTab />}
        {activeTab === 'sistema' && activeSistemaSubTab === 'lojas' && user.role === 'admin' && <LojasGestao />}
        {activeTab === 'sistema' && activeSistemaSubTab === 'xd-clientes' && user.role === 'admin' && <XDClientesImportTab />}
        {activeTab === 'sistema' && activeSistemaSubTab === 'multipreco' && user.role === 'admin' && <MultiPrecoDesviosTab />}
        {activeTab === 'sistema' && activeSistemaSubTab === 'manutencao' && user.role === 'admin' && <SistemaManutencaoTab />}
        {activeTab === 'sistema' && activeSistemaSubTab === 'publicar-app' && user.role === 'admin' && <PublicarAppTab />}

        {/* 17. Website - gestão de conteúdos */}
        {activeTab === 'website' && user.role === 'admin' && <GestaoSiteTab subTab={activeWebsiteSubTab} />}

        {/* 18. Box Janeira - reservas anti-desperdicio */}
        {activeTab === 'box' && (user.role === 'admin' || user.role === 'responsavel' || user.role === 'colaborador') && <BoxJaneiraAdmin user={user} />}

        {/* Meu Perfil (responsaveis only) */}
        {activeTab === 'meu-perfil' && (user.role === 'responsavel' || user.role === 'colaborador') && <MeuPerfilContainer />}

        {/* Hidden routes - internal navigation targets */}
        {activeTab === 'objetivos' && <ObjetivosTab />}
        </Suspense>
        </TabErrorBoundary>
      </main>

      {/* Modal Alertas Login */}
      <AlertasLoginModal />
      
      {/* Modal de Fecho de Turno (Registo de Jogos) */}
      {showFechoTurnoModal && (
        <Suspense fallback={null}>
        <FechoTurnoTab 
          autoOpenModal={true}
          onModalClose={() => {
            setShowFechoTurnoModal(false);
            // Recarregar se estiver na aba de jogos
            if (activeTab === 'jogos') {
              window.location.reload();
            }
          }}
        />
        </Suspense>
      )}
    </div>
  );
};

export default AdminDashboard;
